/**
 * Send diagnostics (parity 06-058, 05-061 and the diagnostics half of 02-059; MB decisions 5, 102, 104; spec
 * docs/specs/send-diagnostics.md). In Teslyn Field's Help the person writes what went wrong, ticks what to include and
 * presses Send. Teslyn Field makes one ZIP on the laptop (`apps/field/src-tauri/src/diagnostics.rs`): notes, project
 * details and versions, the app log, a picture of Teslyn Field's own window, the open project's datasets, the
 * exceedance workbook, the raw basemag files ticked and the map content, every text in it redacted as the app log is
 * (logins, keys, tokens, passwords and email addresses never). It goes to the organisation's own Teslyn Cloud storage
 * (bucket `diagnostics`), only when the person presses Send, never with a built-in password, and only while online.
 * The organisation's tenant admins download or delete packages on Teslyn Cloud's "Diagnostics" page; each is removed
 * for good 30 days after it was sent (the hourly purge).
 */
import { exportSizeText } from './tenant-export.js';

/** The private Storage bucket of diagnostics packages (20261027004000_diagnostics.sql). */
export const DIAGNOSTICS_BUCKET = 'diagnostics';

/** The largest package Teslyn Cloud takes: the bucket's limit, 200 MB as the file archive's. */
export const DIAGNOSTICS_MAX_BYTES = 200 * 1024 * 1024;

/** Days a package is kept after it was sent, then removed for good by the hourly purge. */
export const DIAGNOSTICS_KEEP_DAYS = 30;

/** The longest notes the person can write (the database's check). */
export const DIAGNOSTICS_NOTES_MAX = 4000;

/** The `audit_event.action` of a package sent and of one deleted by a tenant admin. */
export const DIAGNOSTICS_SENT_ACTION = 'diagnostics.sent';
export const DIAGNOSTICS_DELETED_ACTION = 'diagnostics.deleted';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** `tenants/<tenant>/diagnostics/<package>.zip`, the only names the bucket's policies accept. */
export function diagnosticsObjectPath(tenantId: string, packageId: string): string {
  if (!UUID.test(tenantId) || !UUID.test(packageId))
    throw new Error('A diagnostics package needs the organisation and package ids.');
  return `tenants/${tenantId}/diagnostics/${packageId}.zip`;
}

export function isDiagnosticsObjectPath(path: string): boolean {
  const m = /^tenants\/([^/]+)\/diagnostics\/([^/]+)\.zip$/.exec(path);
  return !!m && UUID.test(m[1]!) && UUID.test(m[2]!);
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** `F26048_diagnostics_2026-10-27_1405.zip` in the laptop's local time; `Teslyn_…` with no project open. */
export function diagnosticsFileName(projectNumber: string | null, at: Date): string {
  const stem =
    (projectNumber ?? '')
      .trim()
      .replace(/[^A-Za-z0-9-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'Teslyn';
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `${stem}_diagnostics_${day}_${pad(at.getHours())}${pad(at.getMinutes())}.zip`;
}

/** What Teslyn Field can put in a package, from `diagnostics_preview`. */
export interface DiagnosticsPreview {
  readonly project: {
    readonly id: string;
    readonly number: string;
    readonly title: string;
    readonly tenant_id: string;
  } | null;
  readonly log: { readonly files: number; readonly bytes: number };
  readonly datasets: readonly DiagnosticsDataset[];
  readonly map: {
    readonly files: readonly {
      readonly kind: 'settings' | 'prefs' | 'layers' | 'hazard' | 'kml';
      readonly name: string;
      readonly bytes: number;
    }[];
    readonly landholders: number;
  };
  readonly max_bytes: number;
}

/** One basemag dataset of the open project; `raw_bytes` null when its file is no longer on the laptop. */
export interface DiagnosticsDataset {
  readonly id: string;
  readonly file_name: string;
  readonly source_format: string;
  readonly station_id: string;
  readonly started_at: string;
  readonly reading_count: number;
  readonly raw_bytes: number | null;
}

export interface DiagnosticsChoices {
  readonly log: boolean;
  /** The size of the window picture when it is ticked, else null. */
  readonly screenshotBytes: number | null;
  readonly datasets: boolean;
  readonly map: boolean;
  /** The datasets whose raw files are ticked. */
  readonly rawIds: readonly string[];
}

/**
 * The package's size before compression, from what is ticked, and whether it is over the limit (Send is not offered
 * then). The datasets list and the workbook are a few KB and not counted; the ZIP is checked again once made.
 */
export function diagnosticsEstimate(
  preview: DiagnosticsPreview,
  c: DiagnosticsChoices,
): { readonly bytes: number; readonly over: boolean; readonly text: string } {
  const raw = preview.datasets
    .filter((d) => c.rawIds.includes(d.id))
    .reduce((n, d) => n + (d.raw_bytes ?? 0), 0);
  const map = c.map ? preview.map.files.reduce((n, f) => n + f.bytes, 0) : 0;
  const bytes = (c.log ? preview.log.bytes : 0) + (c.screenshotBytes ?? 0) + raw + map;
  const limit = preview.max_bytes || DIAGNOSTICS_MAX_BYTES;
  const over = bytes > limit;
  const text = over
    ? `About ${exportSizeText(bytes)}: over the ${exportSizeText(limit).replace('.0 MB', ' MB')} Teslyn Cloud takes. Untick some raw files or the map content.`
    : `About ${exportSizeText(bytes)} before compression; ${exportSizeText(limit).replace('.0 MB', ' MB')} at most.`;
  return { bytes, over, text };
}

const DAY_MS = 86_400_000;

/** When a package sent at `sentAt` is removed for good. */
export function diagnosticsRemovedOn(sentAt: string): Date {
  return new Date(new Date(sentAt).getTime() + DIAGNOSTICS_KEEP_DAYS * DAY_MS);
}

/** "Removed on 26 Nov 2026, in 30 days" for the Teslyn Cloud page. */
export function diagnosticsKeptText(sentAt: string, now: Date): string {
  const on = diagnosticsRemovedOn(sentAt);
  const left = Math.ceil((on.getTime() - now.getTime()) / DAY_MS);
  if (left <= 0) return 'Removed within the hour';
  const date = on.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return `Removed on ${date}, in ${left} ${left === 1 ? 'day' : 'days'}`;
}

export type DiagnosticsProblemCode =
  'offline' | 'signed_out' | 'no_tenant' | 'too_large' | 'denied' | 'server';

/** The words for a package that could not be sent. */
export function diagnosticsProblem(code: DiagnosticsProblemCode, detail?: string): string {
  switch (code) {
    case 'offline':
      return 'You are offline. Send diagnostics needs the internet: connect, then press Send again. Nothing was sent.';
    case 'signed_out':
      return 'Sign in to Teslyn Cloud first (Home, Sign in). Diagnostics go to your organisation’s tenant admins.';
    case 'no_tenant':
      return 'Teslyn does not know your organisation yet. Open a project or sign in again, then press Send.';
    case 'too_large':
      return `The package is over the ${DIAGNOSTICS_MAX_BYTES / (1024 * 1024)} MB Teslyn Cloud takes. Untick some raw files or the map content and press Send again.`;
    case 'denied':
      return 'Teslyn Cloud does not let you send diagnostics for this organisation. Ask a tenant admin to check your roles.';
    case 'server':
      return `Teslyn Cloud did not take the package${detail ? ` (${detail})` : ''}. Nothing was kept; press Send to try again.`;
  }
}

/** "Notes, details and versions; app log (2 files); screenshot; …": what a package holds, from its file names. */
export function diagnosticsContentsText(names: readonly string[]): string {
  const count = (test: (n: string) => boolean): number => names.filter(test).length;
  const parts = ['Notes, details and versions'];
  const logs = count((n) => n.startsWith('logs/'));
  if (logs) parts.push(`app log (${logs} ${logs === 1 ? 'file' : 'files'})`);
  if (names.includes('screenshot.png')) parts.push('screenshot');
  if (names.includes('datasets.json')) parts.push('datasets');
  if (names.includes('exceedances.xlsx')) parts.push('exceedance workbook');
  const raw = count((n) => n.startsWith('basemag/'));
  if (raw) parts.push(`raw basemag files (${raw})`);
  const map = count((n) => n.startsWith('map/'));
  if (map) parts.push(`map content (${map} ${map === 1 ? 'file' : 'files'})`);
  return parts.join('; ');
}
